import { yupResolver } from '@hookform/resolvers/yup';
import {
  GetFormalOffersResponse,
  GetIwocapayPaymentLaterResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Radio, Skeleton } from '@iwoca/orion';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { offerChoiceValidator } from './OfferChoiceValidator';
import { SecurityChecksPrompt } from './SecurityChecksPrompt';
import { SelectedOfferSchedule } from './SelectedOfferSchedule';
import { useDrawDownRepaymentScheduleIwocaPay } from './useDrawDownRepaymentScheduleIwocaPay';
import { useGetSecurityCheckRequirements } from './useGetSecurityCheckRequirements';
import { useSelectedOffer } from './useSelectedOffer';
import { filterAvailableDurationsByPayLink } from './utils/filterAvailableDurationsByPaylink';
import { useGetFormalOffers } from '../../../../api/lending/lapiHooks';
import { useTrackViewedOffers } from '../../../../Buyer/hooks/useTrackViewedOffers';
import { useQueryParam } from '../../../../hooks/useQueryParam';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { trackSelectedOffer } from '../../../../utils/tracking';
import { ApprovedApplicationState } from '../../Dashboard.types';
import {
  is30DaysOffer,
  mapOfferDurationToMonths,
} from '../../utils/mapOfferDurationToMonths';

type FormValues = {
  offerId: string;
};

const PAYMENT_OPTION_INPUT_ID = 'offerId' as const;

export const OfferChoiceForm = ({
  application,
}: {
  application: ApprovedApplicationState;
}) => {
  const { isAnyRequirementVisible } = useGetSecurityCheckRequirements();
  const { stateKey } = useStateKey();
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
  const orderId = useQueryParam('orderId');
  const navigate = useNavigate();
  const payLink = application.payLink;

  const initialFormValues: FormValues = {
    offerId: '',
  };
  const methods = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(offerChoiceValidator),
    mode: 'all',
  });

  const handleSubmit = ({ offerId }: FormValues) => {
    const params = new URLSearchParams();

    params.append('payLinkId', payLink.id);
    params.append('offerId', offerId);

    if (orderId) {
      params.append('orderId', orderId);
    }

    if (stateKey) {
      params.append('state_key', stateKey);
    }

    const overlay = new URLSearchParams(window.location.search).get('overlay');
    if (overlay) {
      params.append('overlay', overlay);
    }

    trackSelectedOffer({
      payLinkId: payLink.id,
      offerIds: formalOffers!.formal_offers.map((offer) => offer.offer_id!),
      offerId,
    });

    navigate(`/pay/checkout/?${params.toString()}`);
  };

  const onSubmit = methods.handleSubmit(handleSubmit);
  const { isValid } = methods.formState;

  const availableProducts = formalOffers
    ? filterAvailableDurationsByPayLink({
        offers: formalOffers.formal_offers,
        payLink,
      })
    : [];

  const selectedOffer = useSelectedOffer(availableProducts, methods.watch);

  useTrackViewedOffers({
    payLinkId: payLink.id,
    formalOffers: formalOffers?.formal_offers,
    loadingApis: loadingFormalOffers,
  });

  if (loadingFormalOffers)
    return (
      <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    );

  if (!formalOffers) return null;

  if (availableProducts.length === 1) {
    const selectedOption = methods.getValues(PAYMENT_OPTION_INPUT_ID);
    if (!selectedOption) {
      methods.setValue(PAYMENT_OPTION_INPUT_ID, availableProducts[0].offer_id!);
    }
  }

  return (
    <div>
      <FormProvider {...methods}>
        <div className="mb-m text-l font-med leading-[24px]">
          Choose a payment option
        </div>
        <form autoComplete="off" className="flex flex-col" onSubmit={onSubmit}>
          <div className="mb-l grid gap-m">
            {availableProducts.map((offer) => (
              <OfferChoiceOption
                key={offer.offer_id}
                offer={offer}
                payLink={payLink}
              />
            ))}
          </div>
          {selectedOffer && (
            <hr className="my-l w-full border-0 border-t border-structure-80" />
          )}
          <SecurityChecksPrompt />
          {selectedOffer && (
            <SelectedOfferSchedule offer={selectedOffer} payLink={payLink} />
          )}
          <div className="flex justify-end">
            <Button className="xs:w-full" disabled={!isValid}>
              {isAnyRequirementVisible
                ? 'Complete security checks'
                : 'Finish checkout'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const OfferChoiceOption = ({
  offer,
  payLink,
}: {
  offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][number];
  payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
  const { register, watch } = useFormContext();
  const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
  const isChecked = watchPaymentOption === offer.offer_id!;
  const id = offer.offer_id!;
  const duration = mapOfferDurationToMonths(offer);

  const {
    monthlyRepayment,
    interestRate,
    loadingDrawDownRepaymentScheduleIwocapay,
  } = useDrawDownRepaymentScheduleIwocaPay({
    offer,
    payLink,
  });

  if (loadingDrawDownRepaymentScheduleIwocapay) {
    return <Skeleton />;
  }

  if (monthlyRepayment === undefined || interestRate === undefined) return null;

  const is30Days = is30DaysOffer(offer);

  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-center gap-m rounded-s border px-m py-xl hover:bg-secondary-95 ${isChecked ? 'border-primary-40' : 'border-structure-80'}`}
    >
      <Radio
        {...register(PAYMENT_OPTION_INPUT_ID)}
        id={id}
        checked={isChecked}
        value={id}
      />
      <span className="font-med text-secondary-20">
        <span>
          {is30Days ? (
            <>
              <b> Pay {displayCurrency(monthlyRepayment)} </b> in 30 days time
            </>
          ) : (
            <>
              <b> Pay {displayCurrency(monthlyRepayment)} </b> per month for{' '}
              {duration} months
            </>
          )}
        </span>
        <span className="break-all px-2xs"> | </span>
        <span className="whitespace-nowrap font-reg">
          {interestRate === 0
            ? 'interest-free'
            : `${(interestRate * 100).toFixed(2)}% per month`}
        </span>
      </span>
    </label>
  );
};
